import { Fragment } from 'react';
import type { Artifact, ArtifactVendor, Axis } from '../types.ts';
import { cellFor } from '../lib/artifact.ts';
import {
  cellMatchesVerdictFilter,
  describeActiveFilters,
  type DegradedColumn,
  type FilterState,
} from '../lib/filters.ts';
import { axisDemandFor, isGapAxis } from '../lib/demand.ts';
import { VerdictBadge } from './VerdictBadge.tsx';
import { DemandChip } from './DemandChip.tsx';
import { Highlight } from './Highlight.tsx';

export interface SelectedCell {
  axis: string;
  company: string;
}

/**
 * Rows = axes (user then market), columns = vendors, cells = verdict keys (spec §3).
 * `axes`/`vendors` are the already-filtered visible sets (v1.1: computed by the parent via
 * lib/filters.ts's axisMatchesFilters/visibleVendors) — this component only renders what it's
 * given and handles per-cell highlight/dimming, so filtering logic stays out of the view layer.
 *
 * `degraded` are the columns of competitors the run could not assess (docs/specs/degraded-runs.md),
 * placed after the survivors. They are the point of the whole degraded design: without them the
 * grid reads as a complete market and our crawl failures read as unserved gaps. They carry no
 * record, so they are not clickable — there is no cell detail behind an unassessed vendor, and
 * everything there is to say (which vendor, and why) is on the column head.
 *
 * Each row header carries a demand key when the run gathered demand (design-system.md, "Demand
 * annotations") and nothing at all when it did not — an empty slot would read as "no demand".
 * Whether the row is a gap is read from the whole matrix, not from the visible columns. The key
 * opens the axis-level demand detail through `onSelectDemand`; like the cell selection, that
 * state is the parent's.
 */
export function ComparisonTable({
  artifact,
  axes,
  vendors,
  degraded,
  filters,
  selected,
  onSelect,
  selectedDemandAxis = null,
  onSelectDemand,
}: {
  artifact: Artifact;
  axes: Axis[];
  vendors: ArtifactVendor[];
  degraded: DegradedColumn[];
  filters: FilterState;
  selected: SelectedCell | null;
  onSelect: (cell: SelectedCell) => void;
  selectedDemandAxis?: string | null;
  onSelectDemand?: (axis: string) => void;
}): JSX.Element {
  let lastSource: Axis['source'] | null = null;
  const columnCount = vendors.length + degraded.length;

  if (!axes.length || !columnCount) {
    return <p className="panel empty-note muted">nothing matches: {describeActiveFilters(filters)}</p>;
  }

  return (
    <div className="panel matrix-panel">
      <div className="scroll-x">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="axis-head">
                <span className="label">Capability</span>
              </th>
              {vendors.map((v) => (
                <th key={v.company}>{v.company}</th>
              ))}
              {degraded.map((d) => (
                <th key={d.slug} className="degraded-col">
                  {d.company}
                  <span className="label degraded-col-mark">not assessed</span>
                  <span className="degraded-col-reason mono">{d.error}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {axes.map((axis) => {
              const groupStart = axis.source !== lastSource;
              lastSource = axis.source;
              const demand = axisDemandFor(artifact, axis.capability);
              return (
                <Fragment key={axis.capability}>
                  {groupStart && (
                    <tr
                      className={`axis-group-row${axis.source === 'market' ? ' axis-group-market' : ''}`}
                      key={`group-${axis.source}`}
                    >
                      <th colSpan={columnCount + 1}>
                        <span className="label">
                          {axis.source === 'user' ? 'Your capabilities' : 'Market-derived capabilities'}
                        </span>
                      </th>
                    </tr>
                  )}
                  <tr key={axis.capability}>
                    <th className="axis-cell" title={axis.description ?? undefined}>
                      <Highlight text={axis.capability} query={filters.query} />
                      {axis.description && (
                        <div className="axis-desc">
                          <Highlight text={axis.description} query={filters.query} />
                        </div>
                      )}
                      {demand && (
                        <div className="axis-demand-chip">
                          <DemandChip
                            verdict={demand.verdict}
                            voices={demand.voices}
                            gap={isGapAxis(artifact.matrix, axis.capability)}
                            axis={axis.capability}
                            onClick={onSelectDemand && (() => onSelectDemand(axis.capability))}
                            pressed={selectedDemandAxis === axis.capability}
                          />
                        </div>
                      )}
                    </th>
                    {vendors.map((v) => {
                      const cell = cellFor(artifact.matrix, axis.capability, v.company);
                      const isSelected =
                        selected?.axis === axis.capability && selected?.company === v.company;
                      const dimmed = cell !== null && !cellMatchesVerdictFilter(cell.verdict, filters);
                      return (
                        <td
                          key={v.company}
                          className={`verdict-cell${isSelected ? ' selected' : ''}${cell ? ' clickable' : ''}${dimmed ? ' filtered-out' : ''}`}
                          onClick={() => cell && onSelect({ axis: axis.capability, company: v.company })}
                        >
                          {cell ? (
                            <>
                              <VerdictBadge verdict={cell.verdict} />
                              {cell.entry && <div className="cell-entry mono">{cell.entry}</div>}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      );
                    })}
                    {degraded.map((d) => {
                      const cell = cellFor(artifact.matrix, axis.capability, d.company);
                      const dimmed = cell !== null && !cellMatchesVerdictFilter(cell.verdict, filters);
                      return (
                        <td
                          key={d.slug}
                          className={`verdict-cell degraded-cell${dimmed ? ' filtered-out' : ''}`}
                        >
                          {cell ? (
                            <VerdictBadge verdict={cell.verdict} />
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
